import React, { useMemo, useState } from 'react';
import { Section } from './Section';
import { Button } from './Button';
import { ShieldCheck, Zap, FileText, ChevronDown, CheckCircle2, Gift, Sparkles, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const CHECKOUT_URLS = {
  base: 'https://noveliahub.com/cart/44086900293668:1',
  bump1: 'https://noveliahub.com/cart/44174972256292:1',
  bump2: 'https://noveliahub.com/cart/44174979563556:1',
  both: 'https://noveliahub.com/cart/44174973009956:1'
};
const BASE_PRICE = 14;
const BUMP_PRICE = 9;
const bonusDetails = [{
  badge: 'BONO #1 — GRATIS',
  image: 'https://cdn.magicpatterns.com/uploads/b7hLTHsW3BzfJxJ33FzSXj/Bono_1.png',
  setup: ['Hay un momento que casi todas vivimos después de comprar una guía.', 'Lo compramos con emoción. Lo descargamos. Lo abrimos.', 'Y entonces aparece la pregunta: "Sí, pero… ¿por dónde empiezo?"', 'Y ahí es donde muchas personas se quedan. No porque no quieran. Sino porque nadie les dio un punto de arranque claro.', 'Este bono existe para que eso no te pase a ti.'],
  valueText: ['El Kit de Arranque 24H te da exactamente lo que necesitas para empezar hoy mismo en menos de 10 minutos.', 'Sin releer el protocolo tres veces. Sin reorganizar toda tu cocina. Sin esperar al "momento perfecto".', 'Solo abres, sigues el orden y arrancas.'],
  bullets: ['Checklist de preparación para las primeras 24 horas', 'Revisión rápida de cocina en 5 minutos — sin complicaciones', 'Mapa simple de lo que haces antes, durante y después de tu primer día', 'Mini tracker para cerrar el día con claridad', 'Versión printable para tenerlo a mano sin estar mirando el celular', 'Incluye un plan de recuperación para cuando el día no salió perfecto'],
  closing: 'Este bono convierte la emoción de empezar en acción real desde el minuto uno.',
  realValue: '$17'
}, {
  badge: 'BONO #2 — GRATIS',
  image: 'https://cdn.magicpatterns.com/uploads/hZqWgx79XpjjwBDueKf5wT/Bono_2.png',
  setup: ['¿Sabes cuál es uno de los momentos más frustrantes cuando quieres hacer un cambio en tu alimentación?', 'Entrar al supermercado sin saber exactamente qué comprar.', 'Caminas por los pasillos. Tomas cosas. Las devuelves. Terminas comprando lo de siempre.', 'Y cuando llegas a casa, te das cuenta de que no tienes lo necesario para seguir el protocolo.', 'Empiezas a improvisar. Y ahí se rompe todo.'],
  valueText: ['Esta lista express elimina ese problema de raíz.', 'No tienes que pensar. No tienes que investigar. No tienes que gastar de más.', 'Entras al súper con la lista en mano, marcas lo que ya tienes, compras lo que te falta y en una sola vuelta tienes todo resuelto.', 'Diseñada específicamente para mujeres que viven en USA y compran en lugares como Walmart o Target.', 'Sin productos raros. Sin ingredientes difíciles de encontrar. Sin gastos extras.'],
  bullets: ['Lista maestra organizada por categorías — proteínas, vegetales, frutas y básicos', 'Plantilla "ya lo tengo / me falta" para no comprar lo que ya está en casa', 'Ruta express de compra para entrar y salir rápido', 'Regla anti-gasto para no llenarte de productos que no vas a usar', 'Checklist final para revisar antes de salir del súper', 'Versión móvil y printable incluidas'],
  closing: 'Este bono hace que el protocolo encaje con tu vida real, no con una versión ideal que no existe.',
  realValue: '$17'
}, {
  badge: 'BONO #3 — GRATIS',
  image: 'https://cdn.magicpatterns.com/uploads/xzacukqjcr5NH4ufsaYA36/Bono_3.png',
  setup: ['La mayoría de las personas no fallan por lo que hacen en la mañana.', 'Fallan por lo que pasa en la noche.', 'Llegan cansadas. Sin estructura. Sin haber revisado nada.', 'Y al día siguiente amanecen improvisando otra vez sin recordar bien dónde dejaron el protocolo o qué se supone que toca hacer.', 'Y así, poco a poco, lo que empezó con mucha energía se va diluyendo.', 'No por falta de ganas. Sino por falta de un cierre.'],
  valueText: ['El Reset Nocturno de 5 Minutos es exactamente ese cierre.', 'Un ritual simple que puedes hacer acostada en tu cama, en el sofá o en la cocina antes de dormir.', 'Sin complicaciones. Sin escribir mucho. Sin sentirte como si tuvieras tarea.', 'En 5 minutos revisas lo que sí hiciste bien, identificas lo que te costó, dejas una mini acción lista para mañana y cierras el día sintiéndote en control.', 'No con culpa. No con frustración. Con claridad.'],
  bullets: ['Ritual de 5 minutos dividido en pasos simples y claros', 'Hoja de reflexión rápida para detectar patrones sin juzgarte', 'Tracker de 3 noches para ver tu progreso de un vistazo', 'Guía de recuperación para noches difíciles o días caóticos', 'Frase de cierre para soltar el día sin culpa', 'Versión printable y versión móvil incluidas'],
  closing: 'Este bono te ayuda a sostener el protocolo incluso cuando la vida no coopera.',
  realValue: '$17'
}];
interface OrderBumpProps {
  selected: boolean;
  onToggle: () => void;
  emoji: string;
  title: string;
  description: React.ReactNode;
  image: string;
  imageAlt: string;
}
function OrderBump({
  selected,
  onToggle,
  emoji,
  title,
  description,
  image,
  imageAlt
}: OrderBumpProps) {
  return <motion.div animate={{
    boxShadow: selected ? '0 0 0 3px rgba(212, 175, 55, 0.6), 0 10px 25px -5px rgba(212, 175, 55, 0.3)' : ['0 0 0 0 rgba(212, 175, 55, 0.0)', '0 0 0 4px rgba(212, 175, 55, 0.25)', '0 0 0 0 rgba(212, 175, 55, 0.0)']
  }} transition={{
    duration: selected ? 0.3 : 2.4,
    repeat: selected ? 0 : Infinity,
    ease: 'easeInOut'
  }} className={`relative bg-amber-50/60 border-2 border-gold rounded-2xl p-5 md:p-6 text-left mb-5 z-10 ${selected ? 'ring-2 ring-gold ring-offset-2 ring-offset-white' : ''}`} data-id="element-59">
      {/* Altamente recomendado ribbon */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full shadow-md flex items-center gap-1 whitespace-nowrap" data-id="element-60">
        <Sparkles className="w-3 h-3" data-id="element-61" />
        Altamente recomendado
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-start mt-2" data-id="element-62">
        <img src={image} alt={imageAlt} className="w-full sm:w-32 md:w-36 h-auto rounded-lg object-cover shadow-md border-2 border-white flex-shrink-0" data-id="element-63" />
        <div className="flex-1" data-id="element-64">
          <h3 className="font-bold text-forest text-base md:text-lg leading-snug mb-2" data-id="element-65">
            {emoji} {title}
          </h3>
          <div className="text-gray-700 text-sm md:text-base leading-relaxed" data-id="element-66">
            {description}
          </div>
        </div>
      </div>

      <label className="mt-4 flex items-start gap-3 cursor-pointer select-none bg-white border-2 border-dashed border-gold rounded-lg p-3 hover:bg-amber-50 transition-colors" data-id="element-67">
        <input type="checkbox" checked={selected} onChange={onToggle} className="w-5 h-5 mt-0.5 accent-gold flex-shrink-0 cursor-pointer" data-id="element-68" />
        <span className="text-forest font-bold text-sm md:text-base leading-snug" data-id="element-69">
          ✅ Sí, añadir a mi orden por solo $9
        </span>
      </label>
    </motion.div>;
}
export function FinalOfferSection() {
  const [showBonuses, setShowBonuses] = useState(false);
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const {
    totalPrice,
    checkoutUrl
  } = useMemo(() => {
    let price = BASE_PRICE;
    let url = CHECKOUT_URLS.base;
    if (bump1 && bump2) {
      price += BUMP_PRICE * 2;
      url = CHECKOUT_URLS.both;
    } else if (bump1) {
      price += BUMP_PRICE;
      url = CHECKOUT_URLS.bump1;
    } else if (bump2) {
      price += BUMP_PRICE;
      url = CHECKOUT_URLS.bump2;
    }
    return {
      totalPrice: price,
      checkoutUrl: url
    };
  }, [bump1, bump2]);
  return <Section id="offer" className="bg-sage pb-24" data-id="element-70">
      <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-gold max-w-3xl mx-auto text-center relative overflow-hidden" data-id="element-71">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gold opacity-10 rounded-bl-full" data-id="element-72"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-forest opacity-5 rounded-tr-full" data-id="element-73"></div>

        <h2 className="font-serif text-3xl md:text-4xl text-forest font-bold mb-6 relative z-10" data-id="element-74">
          Empieza hoy con Adiós Hinchazón: 3-Day Reset
        </h2>
        <p className="text-gray-600 text-lg mb-8 relative z-10" data-id="element-75">
          Accede ahora a tu guía digital en PDF y empieza hoy mismo con un plan
          simple para sentirte más ligera, menos hinchada y más cómoda en tu
          cuerpo.
        </p>

        <img src="https://cdn.magicpatterns.com/uploads/obmiXzA8Rtge2bMWcaqEoG/adios_inchazon_3_Day_Reset.png" alt="Adiós Hinchazón: 3-Day Reset" className="w-64 md:w-80 mx-auto mb-6 relative z-10 drop-shadow-xl" data-id="element-76" />

        <div className="relative z-10 mb-4" data-id="element-77">
          <p className="text-sm font-bold text-forest uppercase tracking-wider mb-4" data-id="element-78">
            + 3 Bonos GRATIS incluidos
          </p>
          <div className="flex items-center justify-center gap-3" data-id="element-79">
            <img src="https://cdn.magicpatterns.com/uploads/b7hLTHsW3BzfJxJ33FzSXj/Bono_1.png" alt="Bono 1 — Kit de Arranque 24H" className="w-20 h-28 md:w-24 md:h-32 object-cover rounded-lg shadow-lg border-2 border-white -rotate-3" data-id="element-80" />
            <img src="https://cdn.magicpatterns.com/uploads/hZqWgx79XpjjwBDueKf5wT/Bono_2.png" alt="Bono 2 — Lista Express de Supermercado" className="w-20 h-28 md:w-24 md:h-32 object-cover rounded-lg shadow-lg border-2 border-white z-10 scale-105" data-id="element-81" />
            <img src="https://cdn.magicpatterns.com/uploads/xzacukqjcr5NH4ufsaYA36/Bono_3.png" alt="Bono 3 — Reset Nocturno de 5 Minutos" className="w-20 h-28 md:w-24 md:h-32 object-cover rounded-lg shadow-lg border-2 border-white rotate-3" data-id="element-82" />
          </div>
        </div>

        <button onClick={() => setShowBonuses(!showBonuses)} className="relative z-10 text-forest-light hover:text-forest font-medium text-sm flex items-center gap-1 mx-auto mb-8 underline underline-offset-2 transition-colors" data-id="element-83">
          {showBonuses ? 'Ocultar detalles' : 'Leer más sobre los bonos'}
          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showBonuses ? 'rotate-180' : ''}`} data-id="element-84" />
        </button>

        <AnimatePresence data-id="element-85">
          {showBonuses && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.4
        }} className="overflow-hidden relative z-10 mb-8" data-id="element-86">
              <div className="space-y-8 text-left" data-id="element-87">
                {bonusDetails.map((bono, index) => <div key={index} className="bg-cream rounded-xl p-6 border border-sage-dark" data-id="element-88">
                    <div className="inline-flex items-center gap-2 bg-gold text-forest font-bold px-3 py-1 rounded-full mb-4 text-xs shadow-sm" data-id="element-89">
                      <Gift className="w-4 h-4" data-id="element-90" />
                      {bono.badge}
                    </div>

                    <div className="flex justify-center mb-6" data-id="element-91">
                      <img src={bono.image} alt={bono.badge} className="w-full max-w-[200px] rounded-xl shadow-md border-2 border-white" data-id="element-92" />
                    </div>

                    <div className="space-y-3 mb-6 text-gray-700 text-base" data-id="element-93">
                      {bono.setup.map((p, i) => <p key={i} data-id="element-94">{p}</p>)}
                    </div>

                    <div className="space-y-3 text-gray-800 text-base font-medium mb-4" data-id="element-95">
                      {bono.valueText.map((p, i) => <p key={i} data-id="element-96">{p}</p>)}
                    </div>

                    <ul className="space-y-2 mb-4" data-id="element-97">
                      {bono.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-2" data-id="element-98">
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-99" />
                          <span className="text-gray-700 text-sm" data-id="element-100">
                            {bullet}
                          </span>
                        </li>)}
                    </ul>

                    <p className="text-base font-serif font-bold text-forest italic mb-3" data-id="element-101">
                      {bono.closing}
                    </p>

                    <div className="flex items-center justify-between bg-forest text-cream px-4 py-3 rounded-lg text-sm" data-id="element-102">
                      <span className="text-cream/70 line-through" data-id="element-103">
                        Valor real: {bono.realValue}
                      </span>
                      <span className="text-gold font-bold text-lg" data-id="element-104">
                        GRATIS
                      </span>
                    </div>
                  </div>)}
              </div>
            </motion.div>}
        </AnimatePresence>

        <div id="offer-price" className="bg-cream p-6 rounded-2xl mb-8 border border-sage-dark relative z-10" data-id="element-105">
          <p className="text-forest font-medium mb-3" data-id="element-106">Hoy por solo</p>
          <div className="inline-block bg-red-500 text-white font-bold text-sm md:text-base px-4 py-1.5 rounded-full mb-4 animate-pulse shadow-md" data-id="element-107">
            🔥 DESCUENTO DEL 63% — OFERTA LIMITADA
          </div>
          <div className="flex items-center justify-center gap-3 mb-2" data-id="element-108">
            <span className="text-2xl md:text-3xl text-gray-400 line-through font-serif" data-id="element-109">
              Antes $37 USD
            </span>
          </div>
          <motion.div key={totalPrice} initial={{
          scale: 0.92,
          opacity: 0.6
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="text-5xl md:text-6xl font-bold text-forest font-serif mb-2" data-id="element-110">
            ${totalPrice} <span className="text-2xl text-gray-500" data-id="element-111">USD</span>
          </motion.div>
        </div>

        <p className="text-forest font-serif italic text-base md:text-lg leading-relaxed mb-5 relative z-10 max-w-xl mx-auto" data-id="element-112">
          Cada día que pasa es un día más sintiéndote hinchada. El protocolo
          tarda 72 horas. Empieza hoy.
        </p>

        <Button href={checkoutUrl} className="w-full md:w-auto text-base sm:text-lg md:text-xl py-5 px-6 md:px-10 mb-3 relative z-10" data-id="element-113">
          Sí, quiero acceso ahora — ${totalPrice} USD
        </Button>

        <p className="text-sm md:text-base text-red-600 font-semibold mb-8 relative z-10 leading-snug" data-id="element-114">
          Precio especial de lanzamiento.
          <br data-id="element-115" />
          Puede subir en cualquier momento.
        </p>

        {/* ORDER BUMPS */}
        <div className="relative z-10 max-w-2xl mx-auto" data-id="element-116">
          <p className="text-forest font-serif italic text-base md:text-lg leading-relaxed mb-6 text-center max-w-xl mx-auto" data-id="element-117">
            Antes de completar tu compra, puedes añadir estos extras opcionales
            para hacer tu reset todavía más fácil.
          </p>
          <OrderBump selected={bump1} onToggle={() => setBump1(!bump1)} emoji="🔒" title="ASEGURA TUS RESULTADOS: Evita volver a hincharte el cuarto día (Solo $9)" image="https://cdn.magicpatterns.com/uploads/gw27cUd7on4YYpuuJ5ucUx/MockupOrderBump1.png" imageAlt="Planificador de Cenas Cero Inflamación" description={<>
                <p className="mb-2" data-id="element-119">
                  El error número uno que cometen las mujeres tras terminar sus
                  72 horas es no saber qué cenar al día siguiente. La
                  inflamación abdominal se "alimenta" de la confusión: si
                  vuelves a tus cenas habituales en USA, tu sistema digestivo
                  colapsará de nuevo.
                </p>
                <p data-id="element-120">
                  Añade el{' '}
                  <strong data-id="element-121">Planificador de Cenas "Cero Inflamación"</strong> a tu
                  orden de hoy. Obtendrás 14 ideas de cenas rápidas (menos de 15
                  min) diseñadas específicamente para mantener tu abdomen plano
                  y tu digestión activa de forma permanente. Solo disponible a
                  este precio aquí.
                </p>
              </>} data-id="element-118" />

          <OrderBump selected={bump2} onToggle={() => setBump2(!bump2)} emoji="☕" title="HACK DEL CAFÉ: Cómo beberlo sin inflamar tu abdomen (Oferta Limitada $9)" image="https://cdn.magicpatterns.com/uploads/9GmdwyybMDsdXDuWcdodah/MockupOrderBump2.png" imageAlt="Hack del Café y Té" description={<>
                <p className="mb-2" data-id="element-123">
                  ¿Sabías que el café tradicional con las cremas procesadas de
                  supermercado en USA es una de las mayores causas de
                  inflamación matutina? No tienes que dejar la cafeína, solo
                  tienes que aprender a "blindarla".
                </p>
                <p data-id="element-124">
                  Con esta guía táctica, aprenderás a preparar tu café o té con
                  3 ingredientes clave que calman el cortisol y activan tu
                  metabolismo desde el primer sorbo. Incluye la lista de{' '}
                  <strong data-id="element-125">"Sustitutos de Crema Amigables"</strong> que venden en
                  Target y Walmart. ¡Bebe tu café favorito sin culpas ni
                  pesadez!
                </p>
              </>} data-id="element-122" />
        </div>

        {/* DYNAMIC ORDER SUMMARY */}
        <div className="relative z-10 bg-gradient-to-br from-forest to-forest-light text-cream rounded-2xl p-6 md:p-8 shadow-xl border-2 border-gold mb-6 text-left" data-id="element-126">
          <p className="text-gold font-bold uppercase tracking-widest text-xs mb-4 text-center" data-id="element-127">
            Resumen de tu orden
          </p>

          <div className="space-y-3 text-base" data-id="element-128">
            <div className="flex justify-between items-center pb-3 border-b border-cream/20" data-id="element-129">
              <span className="text-cream" data-id="element-130">Adiós Hinchazón: 3-Day Reset</span>
              <span className="text-cream/60 line-through" data-id="element-131">$37</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-cream/20" data-id="element-132">
              <span className="text-cream" data-id="element-133">3 Bonos Exclusivos</span>
              <span className="text-cream/60 line-through" data-id="element-134">$51</span>
            </div>

            <AnimatePresence data-id="element-135">
              {bump1 && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="overflow-hidden" data-id="element-136">
                  <div className="flex justify-between items-center pb-3 border-b border-cream/20" data-id="element-137">
                    <span className="text-cream flex items-center gap-2" data-id="element-138">
                      <Sparkles className="w-4 h-4 text-gold" data-id="element-139" />
                      Planificador de Cenas
                    </span>
                    <span className="text-gold font-semibold" data-id="element-140">+ $9</span>
                  </div>
                </motion.div>}
              {bump2 && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="overflow-hidden" data-id="element-141">
                  <div className="flex justify-between items-center pb-3 border-b border-cream/20" data-id="element-142">
                    <span className="text-cream flex items-center gap-2" data-id="element-143">
                      <Sparkles className="w-4 h-4 text-gold" data-id="element-144" />
                      Hack del Café y Té
                    </span>
                    <span className="text-gold font-semibold" data-id="element-145">+ $9</span>
                  </div>
                </motion.div>}
            </AnimatePresence>

            <div className="flex justify-between items-center pt-2" data-id="element-146">
              <span className="text-cream/80 font-semibold uppercase tracking-wider text-sm" data-id="element-147">
                Valor total
              </span>
              <span className="text-cream/60 line-through text-xl" data-id="element-148">$88</span>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t-2 border-gold flex justify-between items-center" data-id="element-149">
            <span className="text-gold font-bold uppercase tracking-wider text-sm md:text-base" data-id="element-150">
              Hoy pagas solo
            </span>
            <motion.span key={totalPrice} initial={{
            scale: 0.9,
            opacity: 0.7
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            duration: 0.25
          }} className="text-gold font-serif font-bold text-3xl md:text-4xl" data-id="element-151">
              ${totalPrice} USD
            </motion.span>
          </div>
        </div>

        <Button href={checkoutUrl} className="w-full md:w-auto text-base sm:text-lg md:text-xl py-5 px-6 md:px-10 mb-6 relative z-10" data-id="element-152">
          Completar Compra — ${totalPrice} USD
        </Button>

        {/* PAYMENT METHODS + TRUST LINE */}
        <div className="relative z-10 mt-4" data-id="element-153">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-154">
            Aceptamos los siguientes métodos de pago
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-5" data-id="element-155">
            {/* Visa */}
            <div className="bg-white border border-gray-200 rounded-md px-3 py-2 shadow-sm flex items-center justify-center w-16 h-10" data-id="element-156">
              <span className="font-extrabold italic text-[#1A1F71] text-base tracking-tight" data-id="element-157">
                VISA
              </span>
            </div>

            {/* Mastercard */}
            <div className="bg-white border border-gray-200 rounded-md px-3 py-2 shadow-sm flex items-center justify-center w-16 h-10" data-id="element-158">
              <div className="relative flex items-center" data-id="element-159">
                <div className="w-5 h-5 rounded-full bg-[#EB001B]" data-id="element-160"></div>
                <div className="w-5 h-5 rounded-full bg-[#F79E1B] -ml-2 mix-blend-multiply" data-id="element-161"></div>
              </div>
            </div>

            {/* American Express */}
            <div className="bg-[#006FCF] border border-[#006FCF] rounded-md px-3 py-2 shadow-sm flex items-center justify-center w-16 h-10" data-id="element-162">
              <span className="font-extrabold text-white text-[10px] leading-none text-center" data-id="element-163">
                AMERICAN
                <br data-id="element-164" />
                EXPRESS
              </span>
            </div>

            {/* Discover */}
            <div className="bg-white border border-gray-200 rounded-md px-2 py-2 shadow-sm flex items-center justify-center gap-1 w-16 h-10" data-id="element-165">
              <span className="font-bold text-gray-800 text-[10px] tracking-tight" data-id="element-166">
                DISC
              </span>
              <div className="w-2 h-2 rounded-full bg-[#FF6000]" data-id="element-167"></div>
              <span className="font-bold text-gray-800 text-[10px] tracking-tight" data-id="element-168">
                VER
              </span>
            </div>

            {/* Diners Club */}
            <div className="bg-white border border-gray-200 rounded-md px-1 py-2 shadow-sm flex items-center justify-center w-16 h-10" data-id="element-169">
              <div className="flex items-center gap-0.5" data-id="element-170">
                <div className="w-3.5 h-3.5 rounded-full bg-[#0079BE]" data-id="element-171"></div>
                <span className="font-bold text-[#0079BE] text-[9px] leading-tight tracking-tight" data-id="element-172">
                  Diners
                  <br data-id="element-173" />
                  Club
                </span>
              </div>
            </div>

            {/* ELO */}
            <div className="bg-black border border-black rounded-md px-3 py-2 shadow-sm flex items-center justify-center w-16 h-10" data-id="element-174">
              <span className="font-bold text-sm tracking-tight italic" data-id="element-175">
                <span className="text-[#FFCB05]" data-id="element-176">e</span>
                <span className="text-[#EE2D31]" data-id="element-177">l</span>
                <span className="text-[#00A4E0]" data-id="element-178">o</span>
              </span>
            </div>

            {/* JCB */}
            <div className="bg-white border border-gray-200 rounded-md px-2 py-2 shadow-sm flex items-center justify-center gap-px w-16 h-10" data-id="element-179">
              <span className="font-extrabold italic text-[#0E4C96] text-base leading-none border-r-2 border-[#0E4C96] pr-0.5" data-id="element-180">
                J
              </span>
              <span className="font-extrabold italic text-[#BF0000] text-base leading-none border-r-2 border-[#BF0000] pr-0.5" data-id="element-181">
                C
              </span>
              <span className="font-extrabold italic text-[#007B3E] text-base leading-none" data-id="element-182">
                B
              </span>
            </div>

            {/* UnionPay */}
            <div className="bg-white border border-gray-200 rounded-md px-1.5 py-2 shadow-sm flex items-center justify-center w-16 h-10" data-id="element-183">
              <span className="font-extrabold text-[9px] leading-tight tracking-tight" data-id="element-184">
                <span className="text-[#E21836]" data-id="element-185">Union</span>
                <span className="text-[#00447C]" data-id="element-186">Pay</span>
              </span>
            </div>

            {/* Shop Pay */}
            <div className="bg-[#5A31F4] border border-[#5A31F4] rounded-md px-3 py-2 shadow-sm flex items-center justify-center w-16 h-10" data-id="element-187">
              <span className="font-bold text-white text-sm tracking-tight" data-id="element-188">
                shop<span className="font-light" data-id="element-189">Pay</span>
              </span>
            </div>

            {/* PayPal */}
            <div className="bg-white border border-gray-200 rounded-md px-3 py-2 shadow-sm flex items-center justify-center w-16 h-10" data-id="element-190">
              <span className="font-bold text-sm tracking-tight" data-id="element-191">
                <span className="text-[#003087]" data-id="element-192">Pay</span>
                <span className="text-[#009CDE]" data-id="element-193">Pal</span>
              </span>
            </div>

            {/* Venmo */}
            <div className="bg-[#3D95CE] border border-[#3D95CE] rounded-md px-3 py-2 shadow-sm flex items-center justify-center w-16 h-10" data-id="element-194">
              <span className="font-bold italic text-white text-sm tracking-tight" data-id="element-195">
                venmo
              </span>
            </div>

            {/* Google Pay */}
            <div className="bg-white border border-gray-200 rounded-md px-3 py-2 shadow-sm flex items-center justify-center gap-1 w-16 h-10" data-id="element-196">
              <span className="font-bold text-xs leading-none" data-id="element-197">
                <span className="text-[#4285F4]" data-id="element-198">G</span>
                <span className="text-[#EA4335]" data-id="element-199">o</span>
                <span className="text-[#FBBC04]" data-id="element-200">o</span>
                <span className="text-[#4285F4]" data-id="element-201">g</span>
                <span className="text-[#34A853]" data-id="element-202">l</span>
                <span className="text-[#EA4335]" data-id="element-203">e</span>
              </span>
              <span className="text-gray-700 font-medium text-xs leading-none" data-id="element-204">
                Pay
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs md:text-sm text-gray-600" data-id="element-205">
            <div className="flex items-center gap-1.5" data-id="element-206">
              <Lock className="w-4 h-4 text-green-600" data-id="element-207" />
              <span className="font-semibold" data-id="element-208">Pago 100% seguro</span>
            </div>
            <span className="text-gray-300" data-id="element-209">·</span>
            <div className="flex items-center gap-1.5" data-id="element-210">
              <Zap className="w-4 h-4 text-gold" data-id="element-211" />
              <span data-id="element-212">Acceso inmediato</span>
            </div>
            <span className="text-gray-300" data-id="element-213">·</span>
            <div className="flex items-center gap-1.5" data-id="element-214">
              <ShieldCheck className="w-4 h-4 text-gold" data-id="element-215" />
              <span data-id="element-216">Garantía Total 7 días</span>
            </div>
          </div>
        </div>
      </div>
    </Section>;
}